import { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useVisits, type Visit } from '@/hooks/use-visits';
import { useNavigate } from 'react-router-dom';
import { NewVisitModal } from '@/components/visits/NewVisitModal';
import { 
  Search,
  Filter,
  Calendar,
  Clock,
  User,
  MapPin,
  Package,
  Plus,
  ArrowUpDown,
  SortAsc,
  SortDesc,
  Eye,
  UserPlus,
  ChevronDown,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { cn } from '@/lib/utils';

// Visit status configuration
const visitStatusConfig = {
  'in_progress': {
    label: 'Devam Ediyor',
    color: 'bg-blue-500/20 text-blue-700 border-blue-200',
    icon: Clock
  },
  'completed': {
    label: 'Tamamlandı',
    color: 'bg-green-500/20 text-green-700 border-green-200',
    icon: Eye
  },
  'cancelled': {
    label: 'İptal Edildi',
    color: 'bg-red-500/20 text-red-700 border-red-200',
    icon: AlertCircle
  },
  'scheduled': {
    label: 'Planlandı',
    color: 'bg-yellow-500/20 text-yellow-700 border-yellow-200',
    icon: Calendar
  }
};

// Visit type configuration
const visitTypeConfig = {
  'inceledi': {
    label: 'Ürünü İnceledi',
    color: 'bg-blue-500/20 text-blue-700'
  },
  'fiyat_talep': {
    label: 'Fiyat Talep Etti',
    color: 'bg-yellow-500/20 text-yellow-700'
  },
  'teklif_verildi': {
    label: 'Teklif Verildi',
    color: 'bg-orange-500/20 text-orange-700'
  },
  'satis_yapildi': {
    label: 'Satış Yapıldı',
    color: 'bg-green-500/20 text-green-700'
  }
};

type SortField = 'date' | 'customer' | 'status' | 'type';
type SortDirection = 'asc' | 'desc';

const Visits = () => {
  const navigate = useNavigate();
  const { visits, isLoading, error, getVisitDisplayName, isTemporaryVisit, isManualVisit, isFaceRecognitionVisit, getVisitSourceText } = useVisits();
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [showNewVisitModal, setShowNewVisitModal] = useState(false);

  // Helper functions
  const getVisitType = (visit: Visit) => {
    const metadata = visit.metadata as any || {};
    return metadata.visit_type || '';
  };

  const getVisitStatus = (visit: Visit) => {
    // If visit has explicit status, use it
    if (visit.status) {
      const status = String(visit.status).toLowerCase();
      if (status === 'pending_label') return 'in_progress';
      if (status === 'labeled') return 'completed';
      if (status === 'ignored') return 'cancelled';
      return status;
    }

    // For visits without explicit status, determine based on timing and completion
    const now = new Date();
    const visitDate = getVisitDate(visit);
    
    // Check if visit has ended (has ended_at)
    const hasEnded = visit.ended_at;
    
    if (hasEnded) {
      return 'completed';
    }
    
    // If visit is in the future, it's scheduled
    if (visitDate > now) {
      return 'scheduled';
    }
    
    // If visit is in the past but has no end time
    if (visitDate < now) {
      // For face recognition visits that should have automatic end times
      if (isFaceRecognitionVisit(visit)) {
        // If it's a face recognition visit from the past without end time, likely cancelled/incomplete
        return 'cancelled';
      }
      // For manual visits in the past without end time, likely completed
      return 'completed';
    }
    
    // If visit is happening now (within reasonable window), it's in progress
    return 'in_progress';
  };

  const getVisitDate = (visit: Visit) => {
    if (visit.started_at) {
      return new Date(visit.started_at);
    }
    return new Date();
  };

  const getDateFilterLabel = (filter: string) => {
    switch (filter) {
      case 'today': return 'Bugün';
      case 'week': return 'Bu Hafta';
      case 'month': return 'Bu Ay';
      case 'all': return 'Tümü';
      default: return 'Tümü';
    }
  };

  // Filter and sort logic
  const filteredAndSortedVisits = visits
    .filter(visit => {
      // Search filter
      if (searchTerm) {
        const customerName = getVisitDisplayName(visit);
        const searchText = `${customerName} ${visit.description || ''} ${visit.location || ''}`.toLowerCase();
        if (!searchText.includes(searchTerm.toLowerCase())) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== 'all' && getVisitStatus(visit) !== statusFilter) {
        return false;
      }

      // Type filter
      if (typeFilter !== 'all' && getVisitType(visit) !== typeFilter) {
        return false;
      }

      // Source filter
      if (sourceFilter !== 'all') {
        const metadata = visit.metadata as any || {};
        const source = visit.source || metadata.source || 'manual';
        if (source !== sourceFilter) {
          return false;
        }
      }

      // Date filter
      if (dateFilter !== 'all') {
        const visitDate = getVisitDate(visit);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        switch (dateFilter) {
          case 'today':
            const visitDateOnly = new Date(visitDate.getFullYear(), visitDate.getMonth(), visitDate.getDate());
            if (visitDateOnly.getTime() !== today.getTime()) return false;
            break;
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            if (visitDate < weekAgo) return false;
            break;
          case 'month':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            if (visitDate < monthAgo) return false;
            break;
        }
      }

      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'date':
          comparison = getVisitDate(a).getTime() - getVisitDate(b).getTime();
          break;
        case 'customer':
          comparison = getVisitDisplayName(a).localeCompare(getVisitDisplayName(b), 'tr');
          break;
        case 'status':
          comparison = getVisitStatus(a).localeCompare(getVisitStatus(b));
          break;
        case 'type':
          comparison = getVisitType(a).localeCompare(getVisitType(b));
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return ArrowUpDown;
    return sortDirection === 'asc' ? SortAsc : SortDesc;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setDateFilter('all');
    setSourceFilter('all');
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || dateFilter !== 'all' || sourceFilter !== 'all';

  if (error) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h1 className="text-h1 font-bold">Ziyaretler</h1>
          </div>
          <Card>
            <CardContent className="p-6 text-center">
              <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Veri Yüklenirken Hata Oluştu</h3>
              <p className="text-muted-foreground">Ziyaret verileri yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-h1 font-bold">Ziyaretler</h1>
            <p className="text-muted-foreground">
              Müşteri ziyaretlerini yönetin ve takip edin
            </p>
          </div>
          <Button 
            onClick={() => setShowNewVisitModal(true)}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Yeni Ziyaret
          </Button>
        </div>

        {/* Search and Filters */}
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Müşteri adı, not veya lokasyon ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Filter Toggle */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-2"
                >
                  <Filter className="w-4 h-4" />
                  Filtreler
                  <ChevronDown className={cn(
                    "w-4 h-4 transition-transform",
                    showFilters && "rotate-180"
                  )} />
                </Button>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    onClick={clearFilters}
                    className="text-sm"
                  >
                    Filtreleri Temizle
                  </Button>
                )}
              </div>

              {/* Filter Controls */}
              {showFilters && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t">
                  {/* Status Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Durum</label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tümü</SelectItem>
                        <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                        <SelectItem value="completed">Tamamlandı</SelectItem>
                        <SelectItem value="cancelled">İptal Edildi</SelectItem>
                        <SelectItem value="scheduled">Planlandı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Type Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Tür</label>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tümü</SelectItem>
                        <SelectItem value="inceledi">Ürünü İnceledi</SelectItem>
                        <SelectItem value="fiyat_talep">Fiyat Talep Etti</SelectItem>
                        <SelectItem value="teklif_verildi">Teklif Verildi</SelectItem>
                        <SelectItem value="satis_yapildi">Satış Yapıldı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Tarih</label>
                    <Select value={dateFilter} onValueChange={setDateFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tümü</SelectItem>
                        <SelectItem value="today">Bugün</SelectItem>
                        <SelectItem value="week">Bu Hafta</SelectItem>
                        <SelectItem value="month">Bu Ay</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Source Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Kaynak</label>
                    <Select value={sourceFilter} onValueChange={setSourceFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tümü</SelectItem>
                        <SelectItem value="manual">Manuel</SelectItem>
                        <SelectItem value="face_recognition">Yüz Tanıma</SelectItem>
                        <SelectItem value="qr_scan">QR Kod</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sort */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Sıralama</label>
                    <div className="flex gap-2">
                      <Select value={sortField} onValueChange={(value: SortField) => setSortField(value)}>
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Tarih</SelectItem>
                          <SelectItem value="customer">Müşteri</SelectItem>
                          <SelectItem value="status">Durum</SelectItem>
                          <SelectItem value="type">Tür</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                      >
                        {sortDirection === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results Summary */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {filteredAndSortedVisits.length} ziyaret gösteriliyor
            {hasActiveFilters && ` (${visits.length} toplam)`}
          </span>
        </div>

        {/* Visits List */}
        {isLoading ? (
          <Card>
            <CardContent className="p-6 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
              <p>Ziyaretler yükleniyor...</p>
            </CardContent>
          </Card>
        ) : filteredAndSortedVisits.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              {hasActiveFilters ? (
                <>
                  <Filter className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Filtre Sonucu Bulunamadı</h3>
                  <p className="text-muted-foreground mb-4">
                    Belirlediğiniz filtrelere uygun ziyaret bulunamadı.
                  </p>
                  <Button variant="outline" onClick={clearFilters}>
                    Filtreleri Temizle
                  </Button>
                </>
              ) : (
                <>
                  <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Henüz Ziyaret Yok</h3>
                  <p className="text-muted-foreground mb-4">
                    Henüz hiç ziyaret kaydı oluşturulmamış.
                  </p>
                  <Button onClick={() => setShowNewVisitModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    İlk Ziyaretinizi Başlatın
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredAndSortedVisits.map((visit) => {
              const visitStatus = getVisitStatus(visit);
              const statusConfig = visitStatusConfig[visitStatus as keyof typeof visitStatusConfig];
              const visitType = getVisitType(visit);
              const typeConfig = visitTypeConfig[visitType as keyof typeof visitTypeConfig];
              const visitDate = getVisitDate(visit);
              const isTemporary = isTemporaryVisit(visit);

              return (
                <Card key={visit.id} className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/visits/${visit.id}`)}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 flex-1">
                        {/* Avatar */}
                        <Avatar className="w-12 h-12">
                          <AvatarFallback className="bg-accent/20 text-accent">
                            {isTemporary ? (
                              <UserPlus className="w-6 h-6" />
                            ) : (
                              getVisitDisplayName(visit).split(' ').map(n => n[0]).join('').slice(0, 2)
                            )}
                          </AvatarFallback>
                        </Avatar>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-medium text-lg">
                              {getVisitDisplayName(visit)}
                            </h3>
                            {isTemporary && (
                              <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-700 border-yellow-200">
                                Geçici
                              </Badge>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {format(visitDate, 'dd MMM yyyy, HH:mm', { locale: tr })}
                            </div>
                            {visit.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {visit.location}
                              </div>
                            )}
                            {visit.product_id && (
                              <div className="flex items-center gap-1">
                                <Package className="w-4 h-4" />
                                Ürün İlişkili
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {getVisitSourceText(visit)}
                              {isFaceRecognitionVisit(visit) && visit.ended_at && (
                                <>
                                  • <Clock className="w-3 h-3" />
                                  Süre: {(() => {
                                    const start = new Date(visit.started_at);
                                    const end = new Date(visit.ended_at);
                                    const diff = end.getTime() - start.getTime();
                                    const minutes = Math.floor(diff / (1000 * 60));
                                    return `${Math.floor(minutes / 60)}s ${minutes % 60}d`;
                                  })()}
                                </>
                              )}
                            </div>
                          </div>

                          {visit.description && (
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                              {visit.description}
                            </p>
                          )}

                          <div className="flex flex-wrap gap-2">
                            {/* Status Badge */}
                            <Badge className={cn("border", statusConfig?.color)}>
                              {statusConfig?.icon && <statusConfig.icon className="w-3 h-3 mr-1" />}
                              {statusConfig?.label || visitStatus}
                            </Badge>

                            {/* Type Badge */}
                            {visitType && typeConfig && (
                              <Badge variant="outline" className={typeConfig.color}>
                                {typeConfig.label}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-4"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/visits/${visit.id}`);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <NewVisitModal
          open={showNewVisitModal}
          onOpenChange={setShowNewVisitModal}
        />
      </div>
    </Layout>
  );
};

export default Visits;
